import { useEffect } from 'react';
import Skeleton from 'react-loading-skeleton';
import { useDispatch } from 'react-redux';
import { generatePath, useNavigate, useParams } from 'react-router-dom';
import { set } from '../../../../../actions/breadcrumbs.actions';
import TextField from '../../../../../components/forms/TextField';
import { ADMIN_SCHOOL_AGODI_BRANCH_BREADCRUMBS } from '../../../../../constants/breadcrumbs.constants';
import {
  ADMIN_ORGANIZATIONS_PATHS,
  ADMIN_PATH,
  ADMIN_SCHOOLS_PATHS,
  ADMIN_TAB_PATHS
} from '../../../../../constants/paths.constants';
import { TOAST_TYPES } from '../../../../../constants/settings.constants';
import formValidators from '../../../../../form-validators';
import { useLevelUnmount } from '../../../../../hooks/admin/useLevelUnmount.hook';
import { useResourceInput } from '../../../../../hooks/useResourceInput';
import { useSingleResource } from '../../../../../hooks/useSingleResource.hook';
import { ADMIN_SCHOOL_AGODI_BRANCH_RESOURCE } from '../../../../../resources/admin/admin.school.agodi.branch.resource';
import adminSchoolsService from '../../../../../services/api/admin.schools.service';
import { schools } from '../../../../../services/bff.api';
import toastService from '../../../../../services/toasts.service';

const AdminSchoolAgodiBranch = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const params = useParams();

  const agodiBranchResource = useSingleResource(params.agodiBranchId, {
    ...ADMIN_SCHOOL_AGODI_BRANCH_RESOURCE,
    api: {
      getById: () =>
        schools.getAgodiBranch(
          params.netId,
          params.managementId,
          params.communityId,
          params.schoolId,
          params.branchId,
          params.agodiBranchId
        )
    },
    updateFn: (agodiId, data, options) =>
      adminSchoolsService.updateAgodiBranch(
        params?.netId,
        params?.managementId,
        params?.communityId,
        params?.schoolId,
        params?.branchId,
        agodiId,
        data,
        options
      )
  });

  const { isFetched, isFetching } = agodiBranchResource;
  const isLoading = !isFetched || isFetching;

  useEffect(() => {
    console.log(`breadcrumbs set on ${ADMIN_SCHOOL_AGODI_BRANCH_RESOURCE.key} level`);
    if (!isFetched || isFetching) {
      dispatch(set(ADMIN_SCHOOL_AGODI_BRANCH_BREADCRUMBS.LOADING()));
    } else {
      dispatch(
        set(
          ADMIN_SCHOOL_AGODI_BRANCH_BREADCRUMBS.LOADED({
            netId: params?.netId,
            managementId: params?.managementId,
            communityId: params?.communityId,
            schoolId: params?.schoolId,
            branchId: params?.branchId,
            netName: agodiBranchResource?.data?.meta?.netName,
            managementName: agodiBranchResource?.data?.meta?.managementName,
            communityName: agodiBranchResource?.data?.meta?.communityName,
            schoolName: agodiBranchResource?.data?.meta?.schoolName,
            branchName: agodiBranchResource?.data?.meta?.branchName,
            agodiBranchName: agodiBranchResource?.data?.meta?.agodiBranchName
          })
        )
      );
    }
  }, [
    dispatch,
    params?.netId,
    params?.managementId,
    params?.communityId,
    params?.schoolId,
    params?.branchId,
    agodiBranchResource?.data?.meta?.netName,
    agodiBranchResource?.data?.meta?.managementName,
    agodiBranchResource?.data?.meta?.communityName,
    agodiBranchResource?.data?.meta?.schoolName,
    agodiBranchResource?.data?.meta?.branchName,
    agodiBranchResource?.data?.meta?.agodiBranchName,
    isFetched,
    isFetching
  ]);

  useEffect(() => {
    if (agodiBranchResource.error) {
      toastService.showToast(
        TOAST_TYPES.ERROR,
        'Kan de agodi vestiging niet laden. Je bent een niveau hoger in de hiërarchie geplaatst'
      );
      navigate(
        generatePath(
          `${ADMIN_PATH}${ADMIN_TAB_PATHS.ORGANIZATIONS.path}${ADMIN_ORGANIZATIONS_PATHS.SCHOOLS.path}${ADMIN_SCHOOLS_PATHS.AGODI_BRANCHES.path}`,
          params
        )
      );
      agodiBranchResource.refetch();
    }
  }, [navigate, params, agodiBranchResource]);

  useLevelUnmount(ADMIN_SCHOOL_AGODI_BRANCH_RESOURCE.key);

  const callsignInput = useResourceInput('name', {
    ...agodiBranchResource,
    validators: { name: formValidators.required }
  });
  const postcodeInput = useResourceInput('postcode', agodiBranchResource);
  const districtInput = useResourceInput('district', agodiBranchResource);
  const streetNameInput = useResourceInput('street', agodiBranchResource);
  const houseNumberInput = useResourceInput('houseNumber', agodiBranchResource);

  return (
    <div className="spo-o-admin-school-agodi-branch">
      <h6 className="u-margin-bottom">
        {isLoading ? <Skeleton /> : agodiBranchResource?.data?.spoEntityData?.agodi?.name ?? ''}
      </h6>
      <div className="row">
        <div className="u-margin-bottom col-xs-12 col-md-6 col-lg-6">
          {isLoading ? <Skeleton /> : <TextField required label="Roepnaam" {...callsignInput} />}
        </div>
      </div>
      <div className="row">
        <div className="u-margin-bottom col-xs-12 col-md-6 col-lg-3">
          {isLoading ? <Skeleton /> : <TextField label="Postcode" readOnly {...postcodeInput} />}
        </div>
        <div className="u-margin-bottom col-xs-12 col-md-6 col-lg-3">
          {isLoading ? <Skeleton /> : <TextField label="Gemeente" readOnly {...districtInput} />}
        </div>
        <div className="u-margin-bottom col-xs-12 col-md-6 col-lg-3">
          {isLoading ? <Skeleton /> : <TextField label="Straatnaam" readOnly {...streetNameInput} />}
        </div>
        <div className="u-margin-bottom col-xs-12 col-md-6 col-lg-3">
          {isLoading ? <Skeleton /> : <TextField label="Huisnummer" readOnly {...houseNumberInput} />}
        </div>
      </div>
    </div>
  );
};

export default AdminSchoolAgodiBranch;
